import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, InjectedFormProps } from "redux-form";
import { RootState } from "../../store";
import {
  setSelectedDocument,
  setwizardPageNumber,
  setSelectedCountry,
  setSelectedRegion,
  setidentityCardFrontendPic,
  setidentityCardBackendPic,
  setdriverLicenseFrontendPic,
  setdriverLicenseBackendPic,
  setotherIdentityBackendPic,
  setotherIdentityFrontendPic,
  setpassportFrontendPic,
  setpassportBackendPic,
  setfirstName,
  setlastName,
} from "../../authslice";
import { Dropdown } from "primereact/dropdown";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import user_icon from "../../../images/reg_user.png";
import identity_verify_icon from "../../../images/identity_verify_icon.png";
import countriesData from "../../../data/countries.json";
import statesData from "../../../data/states.json";
import Spacer from "MyComponents/UI/Spacer";
import { useRef } from "react";
import { setEmail, setUserID } from "MyComponents/authslice";
import { useParams } from "react-router-dom";
import { getUserData } from "Utils/user";
import { ToastBox } from "MyComponents/Toaster";
import { updatePersonalKycDraftStep } from "Utils/personalKycDraft";
import { useTranslation } from "react-i18next";

interface FormData {
  country: string;
  document: string;
}

interface Props { }

const SettingAccountIdentityVerification: React.FC<Props & InjectedFormProps<FormData, Props>> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit } = props;
  const dispatch = useDispatch();

  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);
  const selectedCountryName = useSelector((state: RootState) => state.auth.selectedCountryName);
  const driverLicenseFrontendPic = useSelector((state: RootState) => state.auth.driverLicenseFrontendPic);
  const identityCardFrontendPic = useSelector((state: RootState) => state.auth.identityCardFrontendPic);
  const passportFrontendPic = useSelector((state: RootState) => state.auth.passportFrontendPic);
  const otherIdentityFrontendPic = useSelector((state: RootState) => state.auth.otherIdentityFrontendPic);
  const theme = useSelector((state: RootState) => state.theme.theme);
  const { userId } = useParams<{ userId?: string }>();
  const reduxUserId = useSelector((state: RootState) => state.auth.userID);

  useEffect(() => {
    if (userId && reduxUserId !== userId) {
      dispatch(setUserID(userId));
    }
  }, [userId, reduxUserId]);

  const [documentList, setDocumentList] = useState(
    [
      { name: "Driver License", selected: !!driverLicenseFrontendPic },
      { name: "Identity Card", selected: !!identityCardFrontendPic },
      { name: "Passport", selected: !!passportFrontendPic },
      { name: "Other Identity", selected: !!otherIdentityFrontendPic },
    ] as { name: string; selected: boolean }[]
  );

  const handleDelete = (name: string) => {
    if (name === "Driver License") {
      dispatch(setdriverLicenseFrontendPic(""));
      dispatch(setdriverLicenseBackendPic(""));
    } else if (name === "Identity Card") {
      dispatch(setidentityCardFrontendPic(""));
      dispatch(setidentityCardBackendPic(""));
    } else if (name === "Passport") {
      dispatch(setpassportFrontendPic(""));
      dispatch(setpassportBackendPic(""));
    } else if (name === "Other Identity") {
      dispatch(setotherIdentityFrontendPic(""));
      dispatch(setotherIdentityBackendPic(""));
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      try {
        const _userData = await getUserData(userId);
        if (_userData?.userName) {
          dispatch(setfirstName(_userData?.name?.givenName));
          dispatch(setlastName(_userData?.name?.familyName));
          dispatch(setEmail(_userData.userName));
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };
    fetchUserData();
  }, [userId]);

  const updateDocumentListState = () => {
    setDocumentList(prevList =>
      prevList.map(doc => ({
        ...doc,
        selected:
          doc.name === "Driver License" ? !!driverLicenseFrontendPic :
            doc.name === "Identity Card" ? !!identityCardFrontendPic :
              doc.name === "Passport" ? !!passportFrontendPic :
                doc.name === "Other Identity" ? !!otherIdentityFrontendPic :
                  doc.selected
      }))
    );
  };

  const [selectedCountry, setSelectedCountryState] = useState<string>("");
  const [selectedDocument, setSelectedDocumentState] = useState<string>("");
  const [countryValidation, setCountryValidation] = useState<boolean>(false);
  const [documentValidation, setDocumentValidation] = useState<boolean>(false);
  const hasUploadedAny = !!driverLicenseFrontendPic || !!identityCardFrontendPic || !!passportFrontendPic || !!otherIdentityFrontendPic;

  const options = countriesData.map((country) => ({ name: country.name, image: country.image }));

  const countryOptionTemplate = (option: { name: string; image: string }) => (
    <div className="flex align-items-center">
      {option.image && <img src={option.image} alt={option.name} style={{ width: "20px", marginRight: "5px" }} />}
      {option.name}
    </div>
  );

  const countryValueTemplate = (option: any) => {
    if (option) {
      setCountryValidation(false);
      dispatch(setSelectedCountry(option.name));
      setSelectedCountryState(option.name);
      return (
        <div className="flex align-items-center">
          {option.image && <img src={option.image} alt={option.name} style={{ width: "20px", marginRight: "5px" }} />}
          {option.name}
        </div>
      );
    } else {
      return selectedCountryName ? (
        <div className="flex align-items-center">
          <img
            src={countriesData.find((country) => country.name.toLowerCase() === selectedCountryName.toLowerCase())?.image}
            alt={selectedCountryName}
            style={{ width: "20px", marginRight: "5px" }}
          />
          {selectedCountryName}
        </div>
      ) : (
        t('ui.country_placeholder')
      );
    }
  };

  const handleDocumentClick = (documentName: string) => {
    setDocumentValidation(false);
    setSelectedDocumentState(documentName);
  };

  const getRegion = () => {
    const country = statesData.find((country) => country.name === selectedCountry);
    return country ? country.region : "";
  };

  const submitForm = (formValues: FormData) => {
    const region = getRegion();
    dispatch(setSelectedRegion(region));

    const hasUploadedAny = !!driverLicenseFrontendPic || !!identityCardFrontendPic || !!passportFrontendPic || !!otherIdentityFrontendPic;

    if (!selectedCountry && !hasUploadedAny && !selectedCountryName) {
      setCountryValidation(true);
      return;
    }

    if (!selectedDocument && !hasUploadedAny) {
      setDocumentValidation(true);
      return;
    }

    if (selectedDocument) {
      dispatch(setSelectedDocument(selectedDocument));
      dispatch(setwizardPageNumber(page + 1));
      localStorage.setItem('pageNumber', `${page + 1}`);
      if (reduxUserId) {
        updatePersonalKycDraftStep(reduxUserId, 'identity', {
          country: selectedCountry || selectedCountryName,
          selectedDocument: selectedDocument
        });
        updatePersonalKycDraftStep(reduxUserId, 'wizardState', { pageNumber: page + 1 });
      }
    } else if (hasUploadedAny) {
      dispatch(setwizardPageNumber(page + 2));
      localStorage.setItem('pageNumber', `${page + 2}`);
      if (reduxUserId) {
        updatePersonalKycDraftStep(reduxUserId, 'identity', {
          country: selectedCountry || selectedCountryName,
          selectedDocument: selectedDocument
        });
        updatePersonalKycDraftStep(reduxUserId, 'wizardState', { pageNumber: page + 2 });
      }
    }
  };

  useEffect(() => {
    updateDocumentListState();
  }, [driverLicenseFrontendPic, identityCardFrontendPic, passportFrontendPic, otherIdentityFrontendPic]);

  return (
    <>
      <Container fluid>
        <Row>
          <Col xs={12} md={6} className="px-3 px-md-5">
            <div className="">
              <Row>
                <Col xs={2} md={1}>
                  <img src={user_icon} width={45} alt="user_icon" />
                </Col>
                <Col xs={10} md={11}>
                  <h3 className="ms-2 ms-md-3">{t('profile.identity_verification')}</h3>
                  <p className="fw-normal text-secondary ms-2 ms-md-3">{t('kyc.confirm_secure')}</p>
                </Col>
              </Row>
              <Form noValidate onSubmit={handleSubmit(submitForm)}>
                <Row className="mt-3">
                  <Col>
                    <p className="fs-6 fw-bold mb-2">{t('kyc.select_country_residence')}</p>
                    <Dropdown
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountryState(e.value)}
                      options={options}
                      optionLabel="name"
                      placeholder={t('ui.country_placeholder')}
                      filter
                      valueTemplate={countryValueTemplate}
                      itemTemplate={countryOptionTemplate}
                      className="w-full md:w-14rem w-100 p-2 text-start px-3 border rounded-5 bg-white text-secondary"
                    />
                    {countryValidation && (
                      <small className="text-danger">{t('ui.country_required')}</small>
                    )}
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <p className="fs-6 fw-bold mb-1">{t('kyc.select_doc_option')}</p>
                    <p className="fs-6 fw-normal text-secondary mb-2">{t('kyc.select_doc_desc')}</p>

                    <div className="dropdown">
                      <Button
                        className={`form-control form-select text-start px-3 rounded-5 border bg-white text-secondary ${documentValidation ? "border-danger" : ""}`}
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        variant=""
                      >
                        {selectedDocument ? (
                          <>{selectedDocument}</>
                        ) : hasUploadedAny ? (
                          t('kyc.select_another_doc')
                        ) : (
                          t('kyc.select_doc_ph')
                        )}
                      </Button>
                      <ul className="dropdown-menu w-100 rounded-4 hide-scroll border-0 shadow">
                        <li
                          className={`dropdown-item ${driverLicenseFrontendPic ? "disabled" : ""}`}
                          onClick={() => handleDocumentClick("Driver License")}
                          key={0}
                        >
                          Driving License
                        </li>
                        <li
                          className={`dropdown-item ${identityCardFrontendPic ? "disabled" : ""}`}
                          onClick={() => handleDocumentClick("Identity Card")}
                          key={1}
                        >
                          Identity Card
                        </li>
                        <li
                          className={`dropdown-item ${passportFrontendPic ? "disabled" : ""}`}
                          onClick={() => handleDocumentClick("Passport")}
                          key={2}
                        >
                          Passport
                        </li>
                        <li
                          className={`dropdown-item ${otherIdentityFrontendPic ? "disabled" : ""}`}
                          onClick={() => handleDocumentClick("Other Identity")}
                          key={3}
                        >
                          Other Identity
                        </li>
                      </ul>
                    </div>
                    <div className="d-flex flex-column flex-md-row flex-md-wrap gap-3 mt-3">
                      {documentList
                        .filter((doc) => doc.selected)
                        .map((doc, idx) => (
                          <div
                            key={idx}
                            className="border rounded-4 p-3 d-flex align-items-center w-100"
                            style={{ background: "#F9FAFB" }}
                          >
                            <img
                              src="https://img.icons8.com/ios-filled/50/000000/document--v1.png"
                              alt="doc"
                              width={32}
                              height={32}
                              className="me-3"
                            />
                            <div className="flex-grow-1">
                              <div className="fw-bold">{doc.name}</div>
                              <div className="text-secondary small">94 KB of 94 KB</div>
                            </div>
                            <button
                              className="btn btn-link text-danger p-0 ms-2"
                              onClick={() => handleDelete(doc.name)}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </button>
                          </div>
                        ))}
                    </div>
                    <div id="document_validation" className={!documentValidation ? "d-none" : ""}>
                      <small className="text-danger">{t('kyc.document_required')}</small>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={7} className="d-grid gap-2 mt-3">
                    <Button type="submit" className="btn btn-primary rounded-5">
                      {hasUploadedAny && !selectedDocument
                        ? t('kyc.proceed_face_verify')
                        : t('common.next')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
          <Col md={5} className="d-none d-md-block">
            <Spacer height="100px" />
            <Container className="mt-2 p-3">
              <Row className="mt-2">
                <Col md={1} className="mt-2">
                  <img src={identity_verify_icon} width={50} alt="identity_verify_icon" />
                </Col>
                <Col md={11} className="text-lg-start mt-2">
                  <p className="fw-bold small ms-4">{t('kyc.compliance_note')}</p>
                </Col>
              </Row>
              <Row
                className="justify-content-center p-1 m-1 rounded-2"
                style={{
                  backgroundColor: theme === "light" ? "#F9FAFB" : "#333333",
                  boxShadow: theme === "light" ? "none" : "0px 4px 8px rgba(0, 0, 0, 0.5)",
                }}
              >
                <p className="fw-normal text-black small">{t('kyc.valid_id_desc')}</p>
                <ul className="list-group border-0 bg-transparent">
                  <li className="list-group-item border-0 bg-transparent text-secondary small">{t('kyc.id_name_dob')}</li>
                  <li className="list-group-item border-0 bg-transparent text-secondary small">{t('kyc.id_name_address')}</li>
                </ul>
              </Row>
              <Row
                className="justify-content-center p-1 m-1 my-2 rounded-2"
                style={{
                  backgroundColor: theme === "light" ? "#F9FAFB" : "#333333",
                  boxShadow: theme === "light" ? "none" : "0px 4px 8px rgba(0, 0, 0, 0.5)",
                }}
              >
                <p className="fw-normal mt-3 text-black small">{t('kyc.accepted_docs')}</p>
                <ul className="list-group border-0 bg-transparent">
                  <li className="list-group-item border-0 bg-transparent text-secondary small">{t('kyc.accepted_docs_list')}</li>
                  <li className="list-group-item border-0 bg-transparent text-secondary small">{t('kyc.proof_address_desc')}</li>
                </ul>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const form = reduxForm<FormData, Props>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: "wizard",
  enableReinitialize: true,
})(SettingAccountIdentityVerification);

export default form;
